import { Button } from "@/components/ui/button";
import { useTemplateLoader } from "@/hooks/useTemplateLoader";
import { SequenceTemplate } from "./types";
import { useState } from "react";
import { ChevronDown, ChevronUp, Mail, Newspaper, Calendar, X } from "lucide-react";
import { Card } from "../ui/card";

interface TemplateSelectorProps {
  onTemplateSelect: (template: SequenceTemplate) => void;
  onClose: () => void;
  defaultTemplate?: 'welcome' | 'newsletter' | 'monthly-ev';
}

export const TemplateSelector = ({ 
  onTemplateSelect, 
  onClose,
  defaultTemplate 
}: TemplateSelectorProps) => {
  const { data: templates } = useTemplateLoader(defaultTemplate);
  const [isExpanded, setIsExpanded] = useState(true);

  if (defaultTemplate || !templates?.length) return null;

  const handleTemplateSelect = (templateType: string) => {
    let template: SequenceTemplate;

    if (templateType === 'welcome') {
      template = {
        id: 'welcome-sequence-template',
        name: "Welcome Sequence",
        description: "3-email welcome sequence for new subscribers",
        steps: [
          {
            step_number: 1,
            channel: "email",
            content_template: {
              subject: "🚀 Welcome to EVscoop!",
              body: "Welcome to EVscoop!\n\nWe're excited to have you join our community..."
            },
            delay_hours: 0,
            subject_type: 'static'
          },
          {
            step_number: 2,
            channel: "email",
            content_template: {
              subject: "📚 Getting Started with EVscoop",
              body: "Here's everything you need to know about EVscoop..."
            },
            delay_hours: 24,
            subject_type: 'static'
          },
          {
            step_number: 3,
            channel: "email",
            content_template: {
              subject: "💡 Exclusive EV Industry Insights",
              body: "Get ready for your first industry insights..."
            },
            delay_hours: 48,
            subject_type: 'static'
          }
        ]
      };
    } else if (templateType === 'newsletter') {
      template = {
        id: 'newsletter-sequence-template',
        name: "Newsletter Sequence",
        description: "Weekly EV industry updates",
        steps: [
          {
            step_number: 1,
            channel: "email",
            content_template: {
              subject: "📊 This Week in EVs: {DYNAMIC_TOPIC}",
              body: "Here are this week's top EV industry updates..."
            },
            delay_hours: 0,
            subject_type: 'dynamic'
          }
        ]
      };
    } else {
      template = {
        id: 'monthly-ev-sequence-template',
        name: "Monthly EV Preview",
        description: "Monthly industry deep-dive",
        steps: [
          {
            step_number: 1,
            channel: "email",
            content_template: {
              subject: "🔋 Monthly EV Industry Deep-Dive",
              body: "Your comprehensive monthly EV industry analysis..."
            },
            delay_hours: 0,
            subject_type: 'static'
          }
        ]
      };
    }
    
    onTemplateSelect(template);
  };

  return (
    <Card className="relative p-4">
      <Button
        variant="ghost"
        size="icon"
        onClick={onClose}
        className="absolute right-2 top-2"
      >
        <X className="h-4 w-4" />
      </Button>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
        <Button
          variant="outline"
          onClick={() => handleTemplateSelect('welcome')}
          className="w-full text-sm justify-start gap-2 px-4 py-2 h-auto"
        >
          <Mail className="h-4 w-4" />
          Welcome Sequence
        </Button>
        <Button
          variant="outline"
          onClick={() => handleTemplateSelect('newsletter')}
          className="w-full text-sm justify-start gap-2 px-4 py-2 h-auto"
        >
          <Newspaper className="h-4 w-4" />
          Newsletter Sequence
        </Button>
        <Button
          variant="outline"
          onClick={() => handleTemplateSelect('monthly')}
          className="w-full text-sm justify-start gap-2 px-4 py-2 h-auto"
        >
          <Calendar className="h-4 w-4" />
          Monthly EV Preview
        </Button>
      </div>
    </Card>
  );
};